import { useCallback, useState } from 'react';
import { sanitizeNavigationUrl } from '@bridge/utils';
import { useStytchB2BClient } from '@stytch/nextjs/b2b';
import { useRouter } from 'next/navigation';
import { toast } from 'react-hot-toast';
import Sentry from 'utils/sentry';
import { inactiveEmailToast } from '../_components/InactiveEmailToast';
import { buildAuthenticateURL, buildOtpConfirmPath, getForwardableNextPath } from '../../authenticate/_lib/auth-redirect-helpers';
import { sendDiscoveryMagicLink, startSsoConnection } from '../../authenticate/_lib/stytch-auth-handlers';
import type { LoginEmailFormValues } from '../_schemas/loginFormSchemas';
export interface AuthenticationState {
  isGeneratingLink: boolean;
}
export interface AuthenticationActions {
  handleMagicLinkSubmit: (values: LoginEmailFormValues) => Promise<void>;
  handleSSOSubmit: (values: LoginEmailFormValues) => Promise<void>;
  startOAuthFlow: () => void;
}
export interface UseAuthenticationReturn extends AuthenticationState, AuthenticationActions {}
export interface UseAuthenticationParams {
  nextPath?: string;
}
export const useAuthentication = ({
  nextPath
}: UseAuthenticationParams = {}): UseAuthenticationReturn => {
  const router = useRouter();
  const stytch = useStytchB2BClient();
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const handleMagicLinkSubmit = useCallback(async (values: LoginEmailFormValues) => {
    setIsGeneratingLink(true);
    const email = values.email.toLowerCase();
    try {
      const emailOTPResponse = await sendDiscoveryMagicLink(stytch, email, buildAuthenticateURL(nextPath));
      if (emailOTPResponse.status_code === 200) {
        router.replace(sanitizeNavigationUrl(buildOtpConfirmPath(email)));
      } else {
        setIsGeneratingLink(false);
      }
    } catch (error) {
      setIsGeneratingLink(false);
      const stytchError = error as {
        error_type?: string;
      };
      if (stytchError.error_type === 'inactive_email') {
        inactiveEmailToast();
      } else {
        toast.error('Something went wrong. Please try again.');
      }
      console.error('Magic link authentication failed:', error);
      Sentry.captureException(error);
    }
  }, [stytch, router, nextPath]);
  const handleSSOSubmit = useCallback(async (values_0: LoginEmailFormValues) => {
    setIsGeneratingLink(true);
    const email_0 = values_0.email.toLowerCase();
    try {
      const response = await stytch.sso.discoverConnections(email_0);
      const connectionId = response.connections[0]?.connection_id;
      if (!connectionId) {
        toast.error('No SSO connections found for this email');
        throw new Error('No connection ID found');
      }
      // Forward `next` through SSO the way magic link / OAuth do; omit the
      // overrides for ordinary logins so they keep the Stytch-configured default.
      const redirectUrl = getForwardableNextPath(nextPath) ? buildAuthenticateURL(nextPath) : undefined;
      startSsoConnection(stytch, connectionId, redirectUrl);
    } catch (error_0) {
      setIsGeneratingLink(false);
      console.error('SSO authentication failed:', error_0);
      Sentry.captureException(error_0);
    }
  }, [stytch, nextPath]);
  const startOAuthFlow = useCallback(() => {
    stytch.oauth.google.discovery.start({
      discovery_redirect_url: buildAuthenticateURL(nextPath)
    });
  }, [stytch, nextPath]);
  return {
    isGeneratingLink,
    handleMagicLinkSubmit,
    handleSSOSubmit,
    startOAuthFlow
  };
};